import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  Risk,
  RiskStatus,
  RiskSeverity,
  RiskLikelihood,
  RiskAnalytics,
  RiskMitigation,
} from '@/models/team-risk.model';
import {
  auditCreate,
  auditUpdate,
  auditDelete,
} from '@/utils/audit-integration';
import { AuditEntityType, AuditLogAction } from '@/models/audit-log.model';
import { AuditLogService } from './audit-log.service';

const RISKS_COLLECTION = 'teamRisks';

export class TeamRiskService {
  /**
   * Calculate risk score from severity and likelihood
   */
  private static calculateRiskScore(
    severity: RiskSeverity,
    likelihood: RiskLikelihood
  ): number {
    const severityValues: Record<RiskSeverity, number> = {
      [RiskSeverity.low]: 1,
      [RiskSeverity.medium]: 2,
      [RiskSeverity.high]: 3,
      [RiskSeverity.critical]: 4,
    };

    const likelihoodValues: Record<RiskLikelihood, number> = {
      [RiskLikelihood.veryLow]: 1,
      [RiskLikelihood.low]: 2,
      [RiskLikelihood.medium]: 3,
      [RiskLikelihood.high]: 4,
      [RiskLikelihood.veryHigh]: 5,
    };

    return severityValues[severity] * likelihoodValues[likelihood];
  }

  /**
   * Create a new risk (typically from AI extraction)
   */
  static async createRisk(
    riskData: Partial<Risk>,
    userId: string = 'ai-system',
    userName: string = 'AI System'
  ): Promise<string> {
    const now = Timestamp.now();

    const severity = riskData.severity || RiskSeverity.medium;
    const likelihood = riskData.likelihood || RiskLikelihood.medium;

    const risk: Partial<Risk> = {
      ...riskData,
      status: riskData.status || RiskStatus.draft,
      severity,
      likelihood,
      riskScore: this.calculateRiskScore(severity, likelihood),
      containsPHI: riskData.containsPHI || false,
      phiDataTypes: riskData.phiDataTypes || [],
      tags: riskData.tags || [],
      affectedAreas: riskData.affectedAreas || [],
      mitigations: riskData.mitigations || [],
      relatedDecisionIds: riskData.relatedDecisionIds || [],
      relatedTaskIds: riskData.relatedTaskIds || [],
      createdBy: userId,
      createdByName: userName,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, RISKS_COLLECTION), risk);

    // Audit log
    await auditCreate(
      AuditEntityType.teamRisk,
      docRef.id,
      riskData.title || 'Untitled Risk'
    );

    return docRef.id;
  }

  /**
   * Get risk by ID
   */
  static async getRiskById(id: string): Promise<Risk | null> {
    const docRef = doc(db, RISKS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return this.docToRisk(docSnap.id, docSnap.data());
  }

  /**
   * Get all risks for a meeting
   */
  static async getRisksByMeeting(meetingId: string): Promise<Risk[]> {
    const q = query(
      collection(db, RISKS_COLLECTION),
      where('meetingId', '==', meetingId),
      orderBy('riskScore', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToRisk(doc.id, doc.data()));
  }

  /**
   * Get all risks
   */
  static async getAllRisks(): Promise<Risk[]> {
    const q = query(
      collection(db, RISKS_COLLECTION),
      orderBy('riskScore', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToRisk(doc.id, doc.data()));
  }

  /**
   * Get risks by status
   */
  static async getRisksByStatus(status: RiskStatus): Promise<Risk[]> {
    const q = query(
      collection(db, RISKS_COLLECTION),
      where('status', '==', status),
      orderBy('riskScore', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToRisk(doc.id, doc.data()));
  }

  /**
   * Get risks by severity
   */
  static async getRisksBySeverity(severity: RiskSeverity): Promise<Risk[]> {
    const q = query(
      collection(db, RISKS_COLLECTION),
      where('severity', '==', severity),
      orderBy('likelihood', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToRisk(doc.id, doc.data()));
  }

  /**
   * Get high-risk items (critical severity or high risk score)
   */
  static async getHighRisks(): Promise<Risk[]> {
    const allRisks = await this.getAllRisks();
    return allRisks.filter(
      risk =>
        risk.severity === RiskSeverity.critical ||
        risk.severity === RiskSeverity.high ||
        risk.riskScore >= 12
    );
  }

  /**
   * Search risks by title or description
   */
  static async searchRisks(searchTerm: string): Promise<Risk[]> {
    const allRisks = await this.getAllRisks();
    const lowerSearch = searchTerm.toLowerCase();

    return allRisks.filter(risk =>
      risk.title.toLowerCase().includes(lowerSearch) ||
      risk.description.toLowerCase().includes(lowerSearch) ||
      risk.category?.toLowerCase().includes(lowerSearch) ||
      risk.potentialImpact?.toLowerCase().includes(lowerSearch)
    );
  }

  /**
   * Update risk
   */
  static async updateRisk(
    id: string,
    updates: Partial<Risk>,
    userId?: string
  ): Promise<void> {
    const docRef = doc(db, RISKS_COLLECTION, id);

    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    // Recalculate risk score if severity or likelihood changed
    if (updates.severity || updates.likelihood) {
      const risk = await this.getRiskById(id);
      if (risk) {
        const severity = updates.severity || risk.severity;
        const likelihood = updates.likelihood || risk.likelihood;
        updateData.riskScore = this.calculateRiskScore(severity, likelihood);
      }
    }

    if (userId) {
      updateData.updatedBy = userId;
    }

    await updateDoc(docRef, updateData);

    // Audit log
    await auditUpdate(
      AuditEntityType.teamRisk,
      id,
      updates.title || 'Risk'
    );
  }

  /**
   * Delete risk
   */
  static async deleteRisk(id: string): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    const docRef = doc(db, RISKS_COLLECTION, id);
    await deleteDoc(docRef);

    // Audit log
    await auditDelete(
      AuditEntityType.teamRisk,
      id,
      risk.title
    );
  }

  /**
   * Approve a risk
   */
  static async approveRisk(
    id: string,
    approverId: string,
    approverName: string,
    comments?: string
  ): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    // Update approval workflow
    const workflow = risk.approvalWorkflow || [];
    const currentStep = workflow.find(step => step.status === 'pending');

    if (currentStep) {
      currentStep.status = 'approved';
      currentStep.approverId = approverId;
      currentStep.approverName = approverName;
      currentStep.actionDate = new Date();
      currentStep.comments = comments;
    }

    // Check if all steps are approved
    const allApproved = workflow.every(step => step.status === 'approved');

    await this.updateRisk(id, {
      approvalWorkflow: workflow,
      status: allApproved ? RiskStatus.assessed : risk.status,
      approvedBy: allApproved ? approverId : undefined,
      approvedByName: allApproved ? approverName : undefined,
      approvedAt: allApproved ? new Date() : undefined,
    }, approverId);

    // Audit log
    await AuditLogService.writeLog({
      action: allApproved ? AuditLogAction.riskApproved : AuditLogAction.approvalStepCompleted,
      severity: 'info',
      description: `Risk ${allApproved ? 'approved' : 'approval step completed'}: ${risk.title}`,
      userId: approverId,
      entityType: AuditEntityType.teamRisk,
      entityId: id,
      entityName: risk.title,
      success: true,
      details: {
        approverName,
        allApproved,
        comments,
      },
    });
  }

  /**
   * Reject a risk
   */
  static async rejectRisk(
    id: string,
    approverId: string,
    approverName: string,
    reason: string
  ): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    // Update approval workflow
    const workflow = risk.approvalWorkflow || [];
    const currentStep = workflow.find(step => step.status === 'pending');

    if (currentStep) {
      currentStep.status = 'rejected';
      currentStep.approverId = approverId;
      currentStep.approverName = approverName;
      currentStep.actionDate = new Date();
      currentStep.comments = reason;
    }

    await this.updateRisk(id, {
      approvalWorkflow: workflow,
      status: RiskStatus.draft,
    }, approverId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.riskRejected,
      severity: 'warning',
      description: `Risk rejected: ${risk.title}`,
      userId: approverId,
      entityType: AuditEntityType.teamRisk,
      entityId: id,
      entityName: risk.title,
      success: true,
      details: {
        approverName,
        reason,
      },
    });
  }

  /**
   * Add mitigation strategy
   */
  static async addMitigation(
    id: string,
    mitigation: Omit<RiskMitigation, 'id'>,
    userId?: string
  ): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    const newMitigation: RiskMitigation = {
      ...mitigation,
      id: `mitigation-${Date.now()}`,
      status: 'pending',
    };

    const mitigations = [...risk.mitigations, newMitigation];

    await this.updateRisk(id, { mitigations }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.riskMitigationAdded,
      severity: 'info',
      description: `Mitigation added to risk: ${risk.title}`,
      userId,
      entityType: AuditEntityType.teamRisk,
      entityId: id,
      entityName: risk.title,
      success: true,
      details: {
        mitigation: newMitigation.description,
      },
    });
  }

  /**
   * Update mitigation status
   */
  static async updateMitigationStatus(
    id: string,
    mitigationId: string,
    status: 'pending' | 'in_progress' | 'completed',
    userId?: string
  ): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    const mitigations = risk.mitigations.map(m =>
      m.id === mitigationId
        ? {
            ...m,
            status,
            completedDate: status === 'completed' ? new Date() : m.completedDate,
          }
        : m
    );

    await this.updateRisk(id, { mitigations }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.riskMitigationUpdated,
      severity: 'info',
      description: `Mitigation ${status}: ${risk.title}`,
      userId,
      entityType: AuditEntityType.teamRisk,
      entityId: id,
      entityName: risk.title,
      success: true,
      details: {
        mitigationId,
        status,
      },
    });
  }

  /**
   * Mark risk as mitigating
   */
  static async startMitigation(id: string, userId?: string): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    await this.updateRisk(id, {
      status: RiskStatus.mitigating,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.riskMitigationStarted,
      severity: 'info',
      description: `Risk mitigation started: ${risk.title}`,
      userId,
      entityType: AuditEntityType.teamRisk,
      entityId: id,
      entityName: risk.title,
      success: true,
    });
  }

  /**
   * Mark risk as monitoring
   */
  static async startMonitoring(
    id: string,
    reviewFrequency: number,
    userId?: string
  ): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    const now = new Date();
    const nextReviewDate = new Date(now);
    nextReviewDate.setDate(nextReviewDate.getDate() + reviewFrequency);

    await this.updateRisk(id, {
      status: RiskStatus.monitoring,
      lastReviewDate: now,
      nextReviewDate,
      reviewFrequency,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.riskMonitoringStarted,
      severity: 'info',
      description: `Risk monitoring started: ${risk.title}`,
      userId,
      entityType: AuditEntityType.teamRisk,
      entityId: id,
      entityName: risk.title,
      success: true,
      details: {
        reviewFrequency,
        nextReviewDate: nextReviewDate.toISOString(),
      },
    });
  }

  /**
   * Update risk review
   */
  static async updateReview(
    id: string,
    notes: string,
    userId?: string
  ): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    const now = new Date();
    const nextReviewDate = risk.reviewFrequency
      ? new Date(now.getTime() + risk.reviewFrequency * 24 * 60 * 60 * 1000)
      : undefined;

    await this.updateRisk(id, {
      lastReviewDate: now,
      nextReviewDate,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.riskReviewed,
      severity: 'info',
      description: `Risk reviewed: ${risk.title}`,
      userId,
      entityType: AuditEntityType.teamRisk,
      entityId: id,
      entityName: risk.title,
      success: true,
      details: {
        notes,
        nextReviewDate: nextReviewDate?.toISOString(),
      },
    });
  }

  /**
   * Mark risk as resolved
   */
  static async resolveRisk(
    id: string,
    resolutionNotes: string,
    userId?: string
  ): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    await this.updateRisk(id, {
      status: RiskStatus.resolved,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.riskResolved,
      severity: 'info',
      description: `Risk resolved: ${risk.title}`,
      userId,
      entityType: AuditEntityType.teamRisk,
      entityId: id,
      entityName: risk.title,
      success: true,
      details: {
        resolutionNotes,
      },
    });
  }

  /**
   * Mark risk as realized
   */
  static async realizeRisk(
    id: string,
    notes: string,
    actualImpact?: string,
    actualCost?: number,
    userId?: string
  ): Promise<void> {
    const risk = await this.getRiskById(id);
    if (!risk) {
      throw new Error('Risk not found');
    }

    await this.updateRisk(id, {
      status: RiskStatus.realized,
      realizedAt: new Date(),
      realizedNotes: notes,
      actualImpact,
      actualCost,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.riskRealized,
      severity: 'error',
      description: `Risk realized: ${risk.title}`,
      userId,
      entityType: AuditEntityType.teamRisk,
      entityId: id,
      entityName: risk.title,
      success: true,
      details: {
        notes,
        actualImpact,
        actualCost,
      },
    });
  }

  /**
   * Get risk analytics
   */
  static async getRiskAnalytics(
    dateRange?: { start: Date; end: Date }
  ): Promise<RiskAnalytics> {
    let risks: Risk[];

    if (dateRange) {
      const q = query(
        collection(db, RISKS_COLLECTION),
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
      );
      const snapshot = await getDocs(q);
      risks = snapshot.docs.map(doc => this.docToRisk(doc.id, doc.data()));
    } else {
      risks = await this.getAllRisks();
    }

    const byStatus: Record<RiskStatus, number> = {
      [RiskStatus.draft]: 0,
      [RiskStatus.identified]: 0,
      [RiskStatus.assessed]: 0,
      [RiskStatus.mitigating]: 0,
      [RiskStatus.monitoring]: 0,
      [RiskStatus.resolved]: 0,
      [RiskStatus.realized]: 0,
    };

    const bySeverity: Record<RiskSeverity, number> = {
      [RiskSeverity.low]: 0,
      [RiskSeverity.medium]: 0,
      [RiskSeverity.high]: 0,
      [RiskSeverity.critical]: 0,
    };

    const byLikelihood: Record<RiskLikelihood, number> = {
      [RiskLikelihood.veryLow]: 0,
      [RiskLikelihood.low]: 0,
      [RiskLikelihood.medium]: 0,
      [RiskLikelihood.high]: 0,
      [RiskLikelihood.veryHigh]: 0,
    };

    let totalRiskScore = 0;
    let highSeverityCount = 0;
    let realizedCount = 0;
    let mitigatedCount = 0;

    risks.forEach(risk => {
      byStatus[risk.status]++;
      bySeverity[risk.severity]++;
      byLikelihood[risk.likelihood]++;
      totalRiskScore += risk.riskScore;

      if (risk.severity === RiskSeverity.high || risk.severity === RiskSeverity.critical) {
        highSeverityCount++;
      }

      if (risk.status === RiskStatus.realized) {
        realizedCount++;
      }

      if (risk.status === RiskStatus.resolved || risk.status === RiskStatus.monitoring) {
        mitigatedCount++;
      }
    });

    return {
      totalRisks: risks.length,
      byStatus,
      bySeverity,
      byLikelihood,
      avgRiskScore: risks.length > 0 ? totalRiskScore / risks.length : 0,
      highSeverityCount,
      realizedCount,
      mitigatedCount,
    };
  }

  /**
   * Convert Firestore document to Risk model
   */
  private static docToRisk(id: string, data: any): Risk {
    return {
      id,
      meetingId: data.meetingId || '',
      meetingTitle: data.meetingTitle || '',
      sourceTimestampStart: data.sourceTimestampStart,
      sourceTimestampEnd: data.sourceTimestampEnd,
      sourceTextSnippet: data.sourceTextSnippet,
      title: data.title || '',
      description: data.description || '',
      category: data.category || '',
      severity: data.severity || RiskSeverity.medium,
      likelihood: data.likelihood || RiskLikelihood.medium,
      riskScore: data.riskScore || 0,
      potentialImpact: data.potentialImpact || '',
      affectedAreas: data.affectedAreas || [],
      estimatedCost: data.estimatedCost,
      mitigationStrategy: data.mitigationStrategy,
      mitigationOwner: data.mitigationOwner,
      mitigationOwnerId: data.mitigationOwnerId,
      mitigationDueDate: data.mitigationDueDate?.toDate(),
      mitigations: data.mitigations || [],
      status: data.status || RiskStatus.draft,
      approvalWorkflow: data.approvalWorkflow || [],
      lastReviewDate: data.lastReviewDate?.toDate(),
      nextReviewDate: data.nextReviewDate?.toDate(),
      reviewFrequency: data.reviewFrequency,
      realizedAt: data.realizedAt?.toDate(),
      realizedNotes: data.realizedNotes,
      actualImpact: data.actualImpact,
      actualCost: data.actualCost,
      containsPHI: data.containsPHI || false,
      phiDataTypes: data.phiDataTypes || [],
      aiConfidence: data.aiConfidence || 0,
      aiExtractionNotes: data.aiExtractionNotes,
      tags: data.tags || [],
      relatedDecisionIds: data.relatedDecisionIds || [],
      relatedTaskIds: data.relatedTaskIds || [],
      customFields: data.customFields,
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      approvedBy: data.approvedBy,
      approvedByName: data.approvedByName,
      approvedAt: data.approvedAt?.toDate(),
    };
  }
}
